import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CalendarCheck, CalendarX, Loader2, CheckCheck, Star, MessageSquare, Inbox } from "lucide-react";
import { API } from "@/context/AuthContext";

const POLL_MS = 60_000;

const TYPE_ICON = {
  interview_booked: { icon: CalendarCheck, color: "text-emerald-600 bg-emerald-50" },
  interview_cancelled: { icon: CalendarX, color: "text-red-600 bg-red-50" },
  high_score_candidate: { icon: Star, color: "text-amber-600 bg-amber-50" },
  candidate_replied: { icon: MessageSquare, color: "text-blue-600 bg-blue-50" },
  stale_job: { icon: Inbox, color: "text-slate-600 bg-slate-100" },
};

const formatRelative = (iso) => {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "agora";
    if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`;
    if (diff < 86_400) return `${Math.floor(diff / 3600)} h atrás`;
    if (diff < 7 * 86_400) return `${Math.floor(diff / 86_400)} d atrás`;
    return d.toLocaleDateString("pt-BR");
  } catch {
    return "";
  }
};

const NotificationsBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const loadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/count`);
      setUnread(res.data?.unread || 0);
    } catch (err) {
      // Silent: endpoint may 401 on public routes. Log for debugging only.
      console.debug("Notifications count fetch skipped:", err?.response?.status);
    }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications?limit=15`);
      setItems(res.data || []);
      const unreadCount = (res.data || []).filter((r) => !r.read).length;
      setUnread(unreadCount);
    } catch (err) {
      console.warn("Failed to load notifications:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, POLL_MS);
    return () => clearInterval(t);
  }, []);

  // Click outside closes the dropdown
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = () => {
    if (!open) loadItems();
    setOpen((v) => !v);
  };

  const handleClick = async (n) => {
    try {
      if (!n.read) await axios.post(`${API}/notifications/${n.id}/read`);
    } catch (err) {
      console.warn("Failed to mark notification as read:", err);
    }
    setOpen(false);
    if (n.link) navigate(n.link);
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setUnread((c) => Math.max(0, c - (n.read ? 0 : 1)));
  };

  const handleMarkAll = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      setItems((p) => p.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch (err) {
      console.warn("Failed to mark all notifications as read:", err);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={toggle}
        data-testid="notifications-bell"
        className="relative w-10 h-10 rounded-full bg-white/10 border border-white/20 hover:bg-white/15 grid place-items-center transition-colors"
        title="Notificações"
      >
        <Bell className="w-4 h-4 text-white/85" strokeWidth={2} />
        {unread > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-dropdown"
          className="absolute right-0 mt-2 w-[360px] max-h-[80vh] overflow-hidden bg-white text-slate-900 rounded-xl shadow-2xl border border-slate-200 flex flex-col z-50"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-sm">Notificações</h3>
            {items.some((i) => !i.read) && (
              <button
                type="button"
                onClick={handleMarkAll}
                data-testid="mark-all-read-btn"
                className="text-[11px] text-brand-blue hover:underline inline-flex items-center gap-1"
              >
                <CheckCheck className="w-3 h-3" /> Marcar todas como lidas
              </button>
            )}
          </div>

          {loading ? (
            <div className="p-6 flex items-center justify-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              Sem notificações por enquanto.
            </div>
          ) : (
            <ul className="overflow-y-auto divide-y divide-slate-100">
              {items.map((n) => {
                const meta = TYPE_ICON[n.type] || {
                  icon: Bell,
                  color: "text-slate-500 bg-slate-100",
                };
                const Icon = meta.icon;
                return (
                  <li
                    key={n.id}
                    data-testid={`notif-${n.id}`}
                    onClick={() => handleClick(n)}
                    className={`px-3 py-2.5 flex gap-3 cursor-pointer hover:bg-slate-50 ${
                      !n.read ? "bg-blue-50/40" : ""
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-md grid place-items-center shrink-0 ${meta.color}`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-xs leading-snug ${
                          !n.read ? "font-semibold text-slate-900" : "text-slate-700"
                        }`}
                      >
                        {n.title}
                      </p>
                      {n.body && (
                        <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">
                          {n.body}
                        </p>
                      )}
                      <p className="text-[10px] text-slate-400 mt-1">
                        {formatRelative(n.created_at)}
                      </p>
                    </div>
                    {!n.read && (
                      <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-2" />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
